export type FilterExpression<T extends {}> =
    | ConstFilterExpression<T>
    | UnaryFilterExpression<T>
    | BinaryFilterExpression<T>
    | MultipleValueFilterExpression<T>
    | FeedbackMessageFilterExpression<T>
    | ActionsFilterExpression<T>

export type FilterOperator =
    | ConstFilterOperator
    | UnaryFilterOperator
    | BinaryFilterOperator
    | MultipleValueFilterOperator
    | FeedbackMessageFilterOperator
    | ActionsFilterOperator

export enum ConstFilterOperator {
    eq = "const:eq",
    isOneOf = "const:isOneOf"
}

export enum BinaryFilterOperator {
    between = "binary:between"
}

export enum UnaryFilterOperator {
    eq = "unary:eq",
    contains = "unary:contains",
    le = "unary:le",
    ge = "unary:ge"
}

export enum MultipleValueFilterOperator {
    isOneOf = "multiple:isOneOf",
    isNotOneOf = "multiple:isNotOneOf"
}

export enum FeedbackMessageFilterOperator {
    isMessage = "feedback:message",
    isMessageContains = "feedback:messageContains",
    isOneOfTag = "feedback:isOneOfTag"
}

export enum ActionsFilterOperator {
    firstIncorrect = "actions:firstIncorrect",
    relabeled = "actions:relabeled",
    edited = "actions:edited"
}

export function isMultipleOperator(
    op: FilterOperator
): op is MultipleValueFilterOperator {
    return (
        op === MultipleValueFilterOperator.isOneOf ||
        op === MultipleValueFilterOperator.isNotOneOf
    )
}

export function isUnaryOperator(op: FilterOperator): op is UnaryFilterOperator {
    return (
        op === UnaryFilterOperator.contains ||
        op === UnaryFilterOperator.eq ||
        op === UnaryFilterOperator.ge ||
        op === UnaryFilterOperator.le
    )
}

export function isBinaryOperator(
    op: FilterOperator
): op is BinaryFilterOperator {
    return op === BinaryFilterOperator.between
}

export function isFeedbackMessageOperator(
    op: FilterOperator
): op is FeedbackMessageFilterOperator {
    return (
        op === FeedbackMessageFilterOperator.isMessage ||
        op === FeedbackMessageFilterOperator.isMessageContains ||
        op === FeedbackMessageFilterOperator.isOneOfTag
    )
}

export function isActionsOperator(
    op: FilterOperator
): op is ActionsFilterOperator {
    return (
        op === ActionsFilterOperator.firstIncorrect ||
        op === ActionsFilterOperator.relabeled ||
        op === ActionsFilterOperator.edited
    )
}

export type ConstFilterExpression<T extends {}> =
    | ({
          operator: ConstFilterOperator.eq
      } & { [K in keyof T]: { fieldName: K; value: T[K] } }[keyof T])
    | ({
          operator: ConstFilterOperator.isOneOf
      } & { [K in keyof T]: { fieldName: K; value: T[K][] } }[keyof T])

export type UnaryFilterExpression<T extends {}> = {
    operator: UnaryFilterOperator
} & { [K in keyof T]: { fieldName: K; value: T[K] } }[keyof T]

export type BinaryFilterExpression<T extends {}> = {
    operator: BinaryFilterOperator
} & { [K in keyof T]: { fieldName: K; value: [T[K], T[K]] } }[keyof T]

export type MultipleValueFilterExpression<T extends {}> = {
    operator: MultipleValueFilterOperator
} & { [K in keyof T]: { fieldName: K; value: T[K][] } }[keyof T]

export type FeedbackMessageFilterExpression<T extends {}> = {
    operator: FeedbackMessageFilterOperator
} & { [K in keyof T]: { fieldName: K; value: T[K] } }[keyof T]

export type ActionsFilterExpression<T extends {}> = {
    operator: ActionsFilterOperator
} & { [K in keyof T]: { fieldName: K; value: T[K] } }[keyof T]

export function isUnaryFilter<V>(
    filter: FilterExpression<V>
): filter is UnaryFilterExpression<V> {
    return isUnaryOperator(filter.operator)
}

export function isBinaryFilter<V>(
    filter: FilterExpression<V>
): filter is BinaryFilterExpression<V> {
    return isBinaryOperator(filter.operator)
}

export function isMultipleFilter<V>(
    filter: FilterExpression<V>
): filter is MultipleValueFilterExpression<V> {
    return isMultipleOperator(filter.operator)
}

export function isFeedbackMessageFilter<V>(
    filter: FilterExpression<V>
): filter is FeedbackMessageFilterExpression<V> {
    return isFeedbackMessageOperator(filter.operator)
}

export function isActionsFilter<V>(
    filter: FilterExpression<V>
): filter is ActionsFilterExpression<V> {
    return isActionsOperator(filter.operator)
}
